import { Suspense } from "react";
import { MdOpenInNew } from "react-icons/md";
import { useIntl, FormattedMessage, defineMessage } from "react-intl";
import { Link } from "react-router-dom";
import { useTopicPageQuery } from "./loaders/TopicPage";
import ErrorPage from "./ErrorPage";
import Button from "../components/Button";
import EntityProfilePic from "../components/EntityProfilePic";
import Note from "../components/Note";
import SignupCallout from "../components/SignupCallout";
import { SocialSharingDropdown } from "../components/SocialSharingDropdown";
import SubjectSubscriptionButton from "../components/SubjectSubscriptionButton";
import { TimeAgo } from "../components/TimeAgo";
import TopicComments from "../components/TopicComments";
import TopicCommentsForm from "../components/TopicCommentsForm";
import TopicHelmet from "../components/TopicHelmet";
import TopicLink from "../components/TopicLink";
import UserMarkdown from "../components/UserMarkdown";
import VotableOrderSelect from "../components/VotableOrderSelect";
import VoteDisplay from "../components/VoteDisplay";
import { getTopicPath } from "../utils/routing";
import { useExpectedLocationPath, useVotableOrder } from "../utils/hooks";
import { SiArxiv } from "react-icons/si";
import { FaGithub, FaGoogle } from "react-icons/fa6";

interface WebsiteIconMapEntry {
  icon: JSX.Element;
  name?: string;
}

export default function TopicPage() {
  const intl = useIntl();
  const {
    topic,
    query: { viewer },
    variables: { order: initialOrder },
  } = useTopicPageQuery();
  const [order, setOrder] = useVotableOrder(initialOrder);

  useExpectedLocationPath(topic ? getTopicPath(topic) : null);

  if (!topic) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Data set not found",
        })}
      />
    );
  }
  const hostname = (topic.url && new URL(topic?.url).hostname) || "default";
  const websiteData = websiteIconMap[hostname] || websiteIconMap.default;

  return (
    <>
      <TopicHelmet topic={topic} />
      <div className="flex flex-col pt-2 pb-8">
        <div className="flex flex-row gap-4">
          <div className="flex flex-col gap-2 items-center">
            <EntityProfilePic entity={topic.author} size="10" />
            <Link
              to={`/${topic.author.username}`}
              className="underline text-gray-400"
            >
              {topic.author.username}
            </Link>
            <VoteDisplay subject={topic} kind="vertical" />
          </div>

          <div className="flex flex-col gap-4 flex-1">
            <h3 className="text-sm">
              <FormattedMessage
                defaultMessage="Posted {createdAt}"
                values={{
                  createdAt: <TimeAgo createdAt={new Date(topic.createdAt)} />,
                }}
              />
            </h3>

            <div className="flex flex-row gap-4 items-center justify-between flex-wrap">
              {topic.url ? (
                <Link
                  to={topic.url}
                  target="_blank"
                  className="flex flex-wrap items-center gap-1"
                >
                  <h1 className="text-2xl font-bold font-poppins">
                    {topic.title}
                  </h1>
                  {topic.url && (
                    <Note icon={<MdOpenInNew />} className="text-xs">
                      <FormattedMessage defaultMessage="External link" />
                    </Note>
                  )}
                </Link>
              ) : (
                <h1 className="text-2xl font-bold font-poppins">
                  {topic.title}
                </h1>
              )}

              <div className="flex flex-row items-center gap-6">
                <SubjectSubscriptionButton subject={topic} />

                <SocialSharingDropdown
                  title={intl.formatMessage(
                    { defaultMessage: "Sharing {topicTitle}" },
                    { topicTitle: topic.title },
                  )}
                  injectedText={intl.formatMessage(
                    {
                      defaultMessage:
                        "Let's take look to: `{topicTitle}` on Aqora",
                    },
                    { topicTitle: topic.title },
                  )}
                />

                {topic.viewerCanEdit && (
                  <TopicLink topic={topic} subpath="edit">
                    <Button kind="text">
                      <FormattedMessage defaultMessage="Edit" />
                    </Button>
                  </TopicLink>
                )}
              </div>
            </div>

            <div className="pt-5 pb-10">
              {topic.description ? (
                <UserMarkdown>{topic.description}</UserMarkdown>
              ) : (
                <p className="text-gray-400">
                  <FormattedMessage defaultMessage="No description" />
                </p>
              )}
              {topic.url && (
                <Link to={topic.url} target="_blank" className="mx-12">
                  <Button
                    kind="secondary"
                    className="flex flex-row items-center gap-2"
                  >
                    {websiteData.icon}
                    <FormattedMessage
                      defaultMessage="View {name} page"
                      values={{ name: websiteData.name }}
                    />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        <hr />

        <div className="py-5">
          <TopicCommentsForm topic={topic} viewer={viewer} order={order} />
          <div>
            <FormattedMessage defaultMessage="Order by:" />{" "}
            <VotableOrderSelect value={order} onChange={setOrder} />
          </div>
          <Suspense fallback={<TopicComments.Skeleton />}>
            <TopicComments topic={topic} order={order} />
          </Suspense>
        </div>

        <SignupCallout {...cta} className="sm:w-2/3 self-center" />
      </div>
    </>
  );
}

const websiteIconMap: Record<string, WebsiteIconMapEntry> = {
  "arxiv.org": { icon: <SiArxiv />, name: "arXiv" },
  "github.com": { icon: <FaGithub />, name: "GitHub" },
  "google.com": { icon: <FaGoogle />, name: "Google" },
  default: { icon: <MdOpenInNew /> },
};

const cta = Object.freeze({
  title: defineMessage({
    defaultMessage: `Want to join this discussion?`,
  }),
  body: defineMessage({
    defaultMessage: `Join our community today and start discussing with our members by participating in exciting events, competitions, and challenges. Sign up now to engage with quantum experts!`,
  }),
});
